import { ExternalLink } from 'lucide-react';
import Button from './ui/Button';
import { useOpenKanban } from '../hooks/useOpenKanban';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../lib/utils';

type OpenKanbanButtonProps = {
  variant?: 'nav' | 'profile';
  className?: string;
};

export default function OpenKanbanButton({ variant = 'nav', className }: OpenKanbanButtonProps) {
  const { user } = useAuthStore();
  const { openKanban, opening, error } = useOpenKanban();

  if (user?.role === 'ADMIN') {
    return null;
  }

  if (variant === 'profile') {
    return (
      <div className={cn('space-y-3', className)}>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={openKanban} disabled={opening}>
            <ExternalLink className="w-4 h-4 mr-2" />
            {opening ? 'Checking access…' : 'Open Kanban'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      data-help-id="kanban.open_button"
      type="button"
      variant="ghost"
      size="sm"
      onClick={openKanban}
      disabled={opening}
      title={error || 'Open your agency Kanban workspace in a new tab'}
      aria-label={opening ? 'Opening Kanban' : 'Open Kanban'}
      className={cn(
        'shrink-0 h-9 w-9 p-0 text-glass-text-2 hover:text-glass-text hover:bg-glass-3',
        'md:h-9 md:w-auto md:px-3 md:border md:border-accent2/35 md:bg-accent2/10 md:text-accent2 md:hover:bg-accent2/15 md:hover:border-accent2/50',
        error && 'text-red-400 md:border-red-400/50',
        className
      )}
    >
      <ExternalLink className="w-5 h-5 shrink-0 md:w-4 md:h-4 md:mr-1.5" />
      <span className="hidden md:inline">{opening ? 'Opening…' : 'Open Kanban'}</span>
    </Button>
  );
}
